import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "modal-card" }
const _hoisted_2 = { class: "modal-card-head" }
const _hoisted_3 = { class: "modal-card-title" }
const _hoisted_4 = { class: "modal-card-body modal-card-body-allow-overflow" }
const _hoisted_5 = {
  key: 0,
  class: "notification errors-notification is-danger"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }

import { ref, computed } from "vue";
import type { Option } from "vue3-select-component";
import TextArea from "./fields/text-area.vue";
import NumberField from "./fields/number-field.vue";
import DateField from "./fields/date-field.vue";
import SingleSelect from "./fields/single-select.vue";
import MultiSelect from "./fields/multi-select.vue";
import StaticSingleSelect from "./fields/static-single-select.vue";
import VglistUtils from "../utils";
import type { CompletionStatus } from "../types";

// Helper to convert { id, name } to { value, label } for vue3-select-component
interface GamePurchaseSubmittableData {
  game_purchase: {
    game_id: number;
    user_id: number;
    comments?: string;
    rating?: number | string | null;
    hours_played?: number | string | null;
    replay_count?: number | string | null;
    completion_status?: string | null;
    start_date?: string | null;
    completion_date?: string | null;
    platform_ids?: Array<string | number>;
    store_ids?: Array<string | number>;
  };
}

interface Props {
  id?: number;
  rating?: number | string;
  hours_played?: number | string;
  replay_count?: number | string;
  completion_status?: { value: CompletionStatus; label: string } | null;
  start_date?: string;
  completion_date?: string;
  comments?: string;
  platforms?: Array<{ id: number; name: string }>;
  stores?: Array<{ id: number; name: string }>;
  game?: { id: number; name: string } | null;
  userId: number;
  isActive: boolean;
  gameModalState: "create" | "update" | "createWithGame";
}


export default /*@__PURE__*/_defineComponent({
  __name: 'game-modal',
  props: {
    id: {},
    rating: { default: "" },
    hours_played: { default: "" },
    replay_count: { default: 0 },
    completion_status: {},
    start_date: {},
    completion_date: {},
    comments: { default: "" },
    platforms: { default: () => [] },
    stores: { default: () => [] },
    game: {},
    userId: {},
    isActive: { type: Boolean },
    gameModalState: {}
  },
  emits: ["close", "create", "closeAndRefresh"],
  setup(__props: any, { emit: __emit }) {

function toOption(item: { id: number; name: string }): Option<number> {
  return { value: item.id, label: item.name };
}

// Helper to convert array of { id, name } to Option[]
function toOptions(items: Array<{ id: number; name: string }>): Option<number>[] {
  return items.map(toOption);
}

const completionStatuses: Record<CompletionStatus, string> = {
  unplayed: "Unplayed",
  in_progress: "In Progress",
  paused: "Paused",
  dropped: "Dropped",
  completed: "Completed",
  fully_completed: "100% Completed",
  not_applicable: "N/A"
};

const props = __props;

const emit = __emit;

const errors = ref<string[]>([]);

const gamePurchase = ref({
  comments: props.comments,
  rating: props.rating,
  game: props.game ? toOption(props.game) : (null as Option<number> | null),
  userId: props.userId,
  completion_status: props.completion_status as Option<CompletionStatus> | null,
  start_date: props.start_date,
  hours_played:
    typeof props.hours_played === "string" ? parseFloat(props.hours_played) : props.hours_played,
  replay_count: props.replay_count,
  completion_date: props.completion_date,
  platforms: toOptions(props.platforms),
  stores: toOptions(props.stores)
});

const formData = {
  class: "game_purchase",
  comments: {
    label: "Comments",
    attribute: "comments"
  },
  rating: {
    label: "Rating (out of 100)",
    attribute: "rating"
  },
  hoursPlayed: {
    label: "Hours Played",
    attribute: "hours_played"
  },
  replayCount: {
    label: "Replay Count",
    attribute: "replay_count"
  },
  completionStatus: {
    label: "Completion Status"
  },
  startDate: {
    label: "Start Date",
    attribute: "start_date"
  },
  completionDate: {
    label: "Completion Date",
    attribute: "completion_date"
  },
  platforms: {
    label: "Platforms"
  },
  stores: {
    label: "Stores"
  },
  game: {
    label: "Game"
  }
};

const gameSelected = ref(props.gameModalState !== "create");

const gamePurchasesSubmitUrl = computed(() => {
  return props.gameModalState === "update" ? `/game_purchases/${props.id}` : "/game_purchases";
});

const modalTitle = computed(() => {
  return gamePurchase.value.game?.label ?? "Add a game to your library";
});

const formattedCompletionStatuses = computed(() => {
  return Object.entries(completionStatuses).map((status) => {
    return { label: status[1], value: status[0] };
  });
});

function onClose() {
  emit("close");
}

function onSave() {
  const gp = gamePurchase.value;
  if (!gp.game) {
    errors.value = ["Please select a game"];
    return;
  }

  const submittableData: GamePurchaseSubmittableData = {
    game_purchase: {
      game_id: gp.game.value, // Extract ID from Option format
      user_id: gp.userId
    }
  };

  if (gp.comments) {
    submittableData.game_purchase.comments = gp.comments;
  }

  if (gp.rating) {
    submittableData.game_purchase.rating = gp.rating;
  }

  if (
    (typeof gp.hours_played === "string" && gp.hours_played !== "") ||
    (typeof gp.hours_played === "number" && !isNaN(gp.hours_played))
  ) {
    submittableData.game_purchase.hours_played = gp.hours_played;
  }

  if (
    gp.replay_count !== "" &&
    gp.replay_count !== null &&
    typeof gp.replay_count !== "undefined"
  ) {
    submittableData.game_purchase.replay_count = gp.replay_count;
  }

  if (gp.completion_status?.value !== undefined) {
    submittableData.game_purchase.completion_status = gp.completion_status.value;
  }

  if (gp.start_date !== "" && gp.start_date !== null) {
    submittableData.game_purchase.start_date = gp.start_date;
  }

  if (gp.completion_date !== "" && gp.completion_date !== null) {
    submittableData.game_purchase.completion_date = gp.completion_date;
  }

  // Extract IDs from Option format (value property contains the ID)
  if (gp.platforms.length !== 0) {
    submittableData.game_purchase.platform_ids = gp.platforms.map((p) => p.value);
  }

  if (gp.stores.length !== 0) {
    submittableData.game_purchase.store_ids = gp.stores.map((s) => s.value);
  }

  (
    [
      "comments",
      "rating",
      "hours_played",
      "completion_status",
      "start_date",
      "completion_date"
    ] as Array<keyof GamePurchaseSubmittableData["game_purchase"]>
  ).forEach((property) => {
    let value = property === "comments" ? "" : null;
    if (typeof submittableData.game_purchase[property] === "undefined") {
      (submittableData.game_purchase as any)[property] = value;
    }
  });

  if (submittableData.game_purchase.replay_count === undefined) {
    submittableData.game_purchase.replay_count = 0;
  }

  let method: "POST" | "PUT" = "POST";
  if (props.gameModalState === "create" || props.gameModalState === "createWithGame") {
    method = "POST";
  } else if (props.gameModalState === "update") {
    method = "PUT";
  }

  VglistUtils.authenticatedFetch(
    gamePurchasesSubmitUrl.value,
    method,
    JSON.stringify(submittableData)
  )
    .then((gamePurchase) => {
      emit("create", gamePurchase);
      emit("closeAndRefresh");
    })
    .catch((errorsResp) => {
      errors.value = errorsResp;
      const submitButton = document.querySelector(".js-submit-button");
      if (submitButton) {
        // Add a class to the button to indicate an error, then remove it.
        submitButton.classList.add("js-submit-button-error");
        setTimeout(() => {
          submitButton.classList.remove("js-submit-button-error");
        }, 2000);
      }
    });
}

function selectGame() {
  gameSelected.value = true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["modal", { 'is-active': __props.isActive }])
  }, [
    _createElementVNode("div", {
      onClick: onClose,
      class: "modal-background"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("header", _hoisted_2, [
        _createElementVNode("p", _hoisted_3, _toDisplayString(modalTitle.value), 1),
        _createElementVNode("button", {
          onClick: onClose,
          class: "delete",
          "aria-label": "close"
        })
      ]),
      _createElementVNode("section", _hoisted_4, [
        (errors.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("p", null, _toDisplayString(errors.value.length > 1 ? "Errors" : "An error") + " prevented this game from being added to your library: ", 1),
              _createElementVNode("ul", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (error) => {
                  return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true),
        (gameSelected.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(SingleSelect, {
                label: formData.game.label,
                modelValue: gamePurchase.value.game,
                "onUpdate:modelValue": [
                  _cache[0] || (_cache[0] = ($event: any) => ((gamePurchase.value.game) = $event)),
                  selectGame
                ],
                "search-path-identifier": 'games',
                disabled: true
              }, null, 8, ["label", "modelValue"]),
              _createVNode(StaticSingleSelect, {
                label: formData.completionStatus.label,
                modelValue: gamePurchase.value.completion_status,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((gamePurchase.value.completion_status) = $event)),
                options: formattedCompletionStatuses.value
              }, null, 8, ["label", "modelValue", "options"]),
              _createVNode(NumberField, {
                "form-class": formData.class,
                attribute: formData.rating.attribute,
                label: formData.rating.label,
                required: false,
                max: 100,
                modelValue: gamePurchase.value.rating,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((gamePurchase.value.rating) = $event))
              }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
              _createVNode(NumberField, {
                "form-class": formData.class,
                attribute: formData.hoursPlayed.attribute,
                label: formData.hoursPlayed.label,
                required: false,
                modelValue: gamePurchase.value.hours_played,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((gamePurchase.value.hours_played) = $event))
              }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
              _createVNode(NumberField, {
                "form-class": formData.class,
                attribute: formData.replayCount.attribute,
                label: formData.replayCount.label,
                required: false,
                modelValue: gamePurchase.value.replay_count,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((gamePurchase.value.replay_count) = $event))
              }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
              _createVNode(TextArea, {
                "form-class": formData.class,
                attribute: formData.comments.attribute,
                label: formData.comments.label,
                required: false,
                modelValue: gamePurchase.value.comments,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((gamePurchase.value.comments) = $event))
              }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
              _createVNode(DateField, {
                "form-class": formData.class,
                attribute: formData.startDate.attribute,
                label: formData.startDate.label,
                required: false,
                modelValue: gamePurchase.value.start_date,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((gamePurchase.value.start_date) = $event))
              }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
              _createVNode(DateField, {
                "form-class": formData.class,
                attribute: formData.completionDate.attribute,
                label: formData.completionDate.label,
                required: false,
                modelValue: gamePurchase.value.completion_date,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((gamePurchase.value.completion_date) = $event))
              }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
              _createVNode(MultiSelect, {
                label: formData.platforms.label,
                modelValue: gamePurchase.value.platforms,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((gamePurchase.value.platforms) = $event)),
                "search-path-identifier": 'platforms'
              }, null, 8, ["label", "modelValue"]),
              _createVNode(MultiSelect, {
                label: formData.stores.label,
                modelValue: gamePurchase.value.stores,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((gamePurchase.value.stores) = $event)),
                "search-path-identifier": 'stores'
              }, null, 8, ["label", "modelValue"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(SingleSelect, {
                label: formData.game.label,
                modelValue: gamePurchase.value.game,
                "onUpdate:modelValue": [
                  _cache[10] || (_cache[10] = ($event: any) => ((gamePurchase.value.game) = $event)),
                  selectGame
                ],
                "search-path-identifier": 'games'
              }, null, 8, ["label", "modelValue"])
            ]))
      ]),
      _createElementVNode("footer", { class: "modal-card-foot" }, [
        _createElementVNode("button", {
          onClick: onSave,
          class: "button is-primary js-submit-button"
        }, "Save changes"),
        _createElementVNode("button", {
          onClick: onClose,
          class: "button ml-10"
        }, "Cancel")
      ])
    ])
  ], 2))
}
}

})